// Who is speaking on an action card.
//
// R18 D2. The backend raises thirty-four different `needs_user_input:*`
// reasons. Five of them are a broker question: the model copies his sentence
// verbatim into `meta.question`, and quoting it back to the advisor is exactly
// right. The other twenty-nine are holds this product composed about its own
// work - a count it would not write, an attachment it could not save, a reply
// it could not deliver - and for those `meta.question` holds OUR sentence, not
// his.
//
// Every surface used the same template for all thirty-four, so an advisor was
// shown our own words in quotation marks under "The broker asked:" and was told
// a broker had said them. This module owns the split so the three surfaces
// cannot drift apart again, and it defaults an UNKNOWN reason to the hold
// framing: holds outnumber questions twenty-nine to five, and a wrong hold
// reads as cautious where a wrong attribution reads as a lie.
//
// The discriminator is already in the payload - `meta.reason` is
// `needs_user_input:<sub_reason>` and `meta.details` is the backend's own
// advisor-facing sentence - so no new backend field is needed.

import { getNeedsUserInputSubreason } from './actionCardDetails';

// The only reasons whose `meta.question` is the broker's own wording.
export const GENUINE_BROKER_QUESTION_SUBREASONS = Object.freeze([
  'client_question',
  'confidential',
  'legal_contract',
  'negotiation',
  'scheduling'
]);

const GENUINE_BROKER_QUESTION_SUBREASON_SET = new Set(GENUINE_BROKER_QUESTION_SUBREASONS);

export const BROKER_QUESTION_FALLBACK_LABEL = 'Broker Question';
export const SYSTEM_HOLD_FALLBACK_LABEL = 'Your Input Needed';
export const BROKER_QUESTION_FALLBACK_DESCRIPTION = 'Broker asked a question that needs your input';
export const SYSTEM_HOLD_FALLBACK_DESCRIPTION = 'SiteSift held this conversation and needs your input.';

// Lead-ins for the four genuine reasons that had bespoke copy; the fifth and
// any future genuine reason gets the plain attribution.
const BROKER_QUESTION_CHAT_TEMPLATES = {
  confidential: question =>
    `The broker asked about your client's identity:\n\n"${question}"\n\nHow would you like me to respond?`,
  client_question: question =>
    `The broker has a question about requirements:\n\n"${question}"\n\nWhat should I tell them?`,
  negotiation: question =>
    `The broker made a negotiation request:\n\n"${question}"\n\nHow would you like to respond?`
};

const DEFAULT_BROKER_QUESTION_CHAT_TEMPLATE = question =>
  `The broker asked:\n\n"${question}"\n\nWhat would you like me to say?`;

// A hold with copy of its own. He promised, he did not ask - quoting him is
// right here, but the framing must not turn the promise into a question.
const SYSTEM_HOLD_CHAT_TEMPLATES = {
  reply_withheld_broker_promised_fields: (_sentence, { question }) => (
    question
      ? `The broker said the missing details are coming:\n\n"${question}"\n\nThe automatic reply was held and nothing was sent. What would you like to say?`
      : null
  )
};

// Our sentence about our own hold is never dressed as a quotation.
const DEFAULT_SYSTEM_HOLD_CHAT_TEMPLATE = sentence =>
  `This is held for your input:\n\n${sentence}\n\nWhat would you like to do?`;

function readText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function isGenuineBrokerQuestionReason(reason) {
  return GENUINE_BROKER_QUESTION_SUBREASON_SET.has(getNeedsUserInputSubreason(reason) || '');
}

/**
 * The one place that decides whether a card is the broker speaking or a hold,
 * and what each surface should say about it.
 *
 * @param {object} meta a notification's `meta` (reason / question / details)
 * @returns {{
 *   isBrokerQuestion: boolean,
 *   subReason: string,
 *   label: string,        fallback title, for a surface with no copy of its own
 *   description: string,  the one-line sentence under the title
 *   chatMessage: ?string  the composer's opening assistant message, or null
 * }}
 */
export function getHoldFraming(meta) {
  const source = meta && typeof meta === 'object' ? meta : {};
  const subReason = getNeedsUserInputSubreason(source.reason) || '';
  const question = readText(source.question);
  const details = readText(source.details);
  const isBrokerQuestion = GENUINE_BROKER_QUESTION_SUBREASON_SET.has(subReason);

  if (isBrokerQuestion) {
    const template = BROKER_QUESTION_CHAT_TEMPLATES[subReason]
      || DEFAULT_BROKER_QUESTION_CHAT_TEMPLATE;
    return {
      isBrokerQuestion: true,
      subReason,
      label: BROKER_QUESTION_FALLBACK_LABEL,
      // His words first: the backend note is a gloss on them.
      description: question || details || BROKER_QUESTION_FALLBACK_DESCRIPTION,
      chatMessage: question ? template(question) : null
    };
  }

  // The backend already wrote this hold's sentence for the advisor, so it wins
  // over the question our own code composed.
  const sentence = details || question;
  const template = SYSTEM_HOLD_CHAT_TEMPLATES[subReason] || DEFAULT_SYSTEM_HOLD_CHAT_TEMPLATE;
  return {
    isBrokerQuestion: false,
    subReason,
    label: SYSTEM_HOLD_FALLBACK_LABEL,
    description: sentence || SYSTEM_HOLD_FALLBACK_DESCRIPTION,
    chatMessage: sentence ? template(sentence, { question, details }) : null
  };
}
